import { useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

import { Loading } from '../components/loading';
import { useSerialPanelTranslation } from './i18n';

export interface SerialCapturePanelProps {
  /** Códigos de serial ya capturados (orden de captura). */
  serials: string[];
  /** Cantidad requerida (M). El conteo objetivo. */
  required: number;
  /** Modo solo lectura (documento fuera de DRAFT). */
  readOnly?: boolean;
  /**
   * Error externo a mostrar inline (p.ej. resultado de `adapter.validateSerial`).
   * El chequeo de duplicado es local y tiene prioridad visual.
   */
  externalError?: string | null;
  /**
   * Hay un `adapter.validateSerial` en vuelo para el último código leído.
   * Mientras dure, el panel lo indica y no acepta otro: el código no entra en
   * la lista hasta que el backend responde, así que sin este bloqueo un
   * escáner rápido encola lecturas sobre una validación sin resolver.
   */
  validating?: boolean;
  /** Notifica la nueva lista de seriales tras agregar/eliminar. */
  onChange: (next: string[]) => void;
  className?: string;
}

function counterClasses(n: number, required: number): string {
  if (n > required)
    return 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
  if (n === required)
    return 'bg-green-50 text-green-800 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
  return 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
}

/**
 * Panel de CAPTURA de seriales (documentos de entrada, `NatureStock = Add`).
 *
 * El operador ingresa seriales uno a uno por teclado o escáner HID (Enter). Hace
 * verificación local de duplicado (case-insensitive) antes de aceptar; la
 * validación de disponibilidad contra el backend se inyecta vía `externalError`.
 * Componente presentacional y controlado: el estado vive en el contenedor.
 */
export function SerialCapturePanel({
  serials,
  required,
  readOnly = false,
  externalError,
  validating = false,
  onChange,
  className,
}: SerialCapturePanelProps) {
  const { t } = useSerialPanelTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [duplicate, setDuplicate] = useState(false);

  const n = serials.length;
  const remaining = Math.max(0, required - n);

  const refocus = () => setTimeout(() => inputRef.current?.focus(), 10);

  const add = () => {
    if (validating) return;
    const code = value.trim();
    if (!code) return;
    const isDup = serials.some((s) => s.toLowerCase() === code.toLowerCase());
    if (isDup) {
      setDuplicate(true);
      refocus();
      return;
    }
    setDuplicate(false);
    onChange([...serials, code]);
    setValue('');
    refocus();
  };

  const remove = (idx: number) => {
    const next = serials.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const inlineError = duplicate
    ? t('serial_panel.duplicate_error')
    : externalError || null;

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {/* Alert informativo (no dismissible) — oculto en read-only */}
      {!readOnly && (
        <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3 text-[13px] leading-relaxed text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
          <InformationCircleIcon className="mt-px h-4 w-4 shrink-0" />
          <span>{t('serial_panel.alert_required')}</span>
        </div>
      )}

      {/* Banner read-only */}
      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
          <LockClosedIcon className="h-3.5 w-3.5" />
          {t('serial_panel.readonly_banner')}
        </div>
      )}

      {/* Input de captura — oculto en read-only */}
      {!readOnly && (
        <div className="flex items-end gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              {t('serial_panel.input_label')}
            </label>
            <input
              ref={inputRef}
              type="text"
              autoFocus
              value={value}
              disabled={validating}
              placeholder={t('serial_panel.input_placeholder')}
              onChange={(e) => {
                setValue(e.target.value);
                if (duplicate) setDuplicate(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
              className={clsx(
                'h-[38px] w-full rounded-lg border-[1.5px] px-3 text-sm text-slate-800 outline-none transition focus:ring-[3px] dark:bg-slate-900 dark:text-slate-100',
                inlineError
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-600',
                validating && 'opacity-60',
              )}
            />
          </div>
          <button
            type="button"
            onClick={add}
            disabled={validating}
            className={clsx(
              'flex h-[38px] shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white transition hover:bg-primary-700',
              'disabled:opacity-60 disabled:hover:bg-primary-600',
            )}
          >
            {validating ? (
              // El botón es `bg-primary-600`: sin `inheritColor` el spinner sería
              // del mismo color que su fondo.
              <Loading
                size="sm"
                layout="inline"
                inheritColor
                ariaLabel={t('serial_panel.validating')}
              />
            ) : (
              <PlusIcon className="h-3.5 w-3.5 stroke-[2.5]" />
            )}
            {t('serial_panel.add')}
          </button>
        </div>
      )}

      {/* Error inline (duplicado o validación externa) */}
      {inlineError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-[13px] text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <ExclamationCircleIcon className="mt-px h-[15px] w-[15px] shrink-0" />
          <span>{inlineError}</span>
        </div>
      )}

      {/* Counter + chip list */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
            {t('serial_panel.captured_section')}
          </span>
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] font-semibold',
              counterClasses(n, required),
            )}
          >
            {t('serial_panel.counter_label', { captured: n, required })}
          </span>
        </div>

        <div
          className={clsx(
            'flex min-h-[44px] flex-wrap gap-2 rounded-lg border-[1.5px] border-dashed border-slate-300 bg-slate-50 p-2.5 dark:border-slate-600 dark:bg-slate-800/40',
            serials.length === 0 && 'items-center justify-center',
          )}
        >
          {serials.length === 0 ? (
            <span className="text-xs italic text-slate-400 dark:text-slate-500">
              {t('serial_panel.chip_empty')}
            </span>
          ) : (
            serials.map((code, i) => (
              <span
                key={`${code}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
              >
                <CheckIcon className="h-3 w-3 stroke-[2.5]" />
                {code}
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={t('serial_panel.remove_aria')}
                    onClick={() => remove(i)}
                    className="ml-0.5 rounded-full opacity-65 transition hover:opacity-100"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Mensaje de conteo cuando aún falta (ayuda contextual) */}
      {!readOnly && n !== required && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {n < required
            ? t('serial_panel.save_tooltip', { count: remaining })
            : t('serial_panel.count_mismatch', { captured: n, required })}
        </p>
      )}
    </div>
  );
}
